import React, { useEffect, useState } from "react";
import { FiRefreshCw, FiLoader } from "react-icons/fi";
import api from "../../services/api";
import ToastManager from "../../components/Toast/ToastManager";
import { useToast } from "../../hooks/useToast";
import SchedulerHealth from "../../components/CronJob/SchedulerHealth";
import JobCard from "../../components/CronJob/JobCard";
import JobDetailsModal from "../../components/CronJob/JobDetailsModal";
import InfoPanel from "../../components/CronJob/InfoPanel";
import {
  CronContainer,
  PageTitle,
  HeaderSection,
  RefreshButton,
  JobsGrid,
  EmptyState,
  LoadingState,
  ErrorState,
} from "./IndexPage.styles";

const CronIndexPage = () => {
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingHealth, setIsLoadingHealth] = useState(false);
  const [error, setError] = useState(null);
  const [healthError, setHealthError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const [executingJobId, setExecutingJobId] = useState(null);
  const [pausingJobId, setPausingJobId] = useState(null);
  const [resumingJobId, setResumingJobId] = useState(null);
  const [disablingJobId, setDisablingJobId] = useState(null);
  const [enablingJobId, setEnablingJobId] = useState(null);
  
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { toasts, removeToast, showSuccess, showError } = useToast();

  const fetchJobs = async (showLoading = true) => {
    if (showLoading) setIsLoadingJobs(true);
    setError(null);
    
    try {
      const response = await api.get("/scheduler/jobs");
      setJobs(response.data);
    } catch (err) {
      console.error("Erro ao buscar jobs:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao carregar jobs agendados.";
      setError(errorMsg);
      if (showLoading) {
        showError(errorMsg);
      }
    } finally {
      if (showLoading) setIsLoadingJobs(false);
    }
  };

  const fetchHealth = async (showLoading = true) => {
    if (showLoading) setIsLoadingHealth(true);
    setHealthError(null);
    
    try {
      const response = await api.get("/scheduler/health");
      setHealth(response.data);
    } catch (err) {
      console.error("Erro ao buscar health:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao verificar status do scheduler.";
      setHealthError(errorMsg);
    } finally {
      if (showLoading) setIsLoadingHealth(false);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    await Promise.all([
      fetchJobs(false),
      fetchHealth(false)
    ]);
    setIsRefreshing(false);
  };

  useEffect(() => {
    fetchJobs();
    fetchHealth();
  }, []);

  const handleExecuteJob = async (jobId) => {
    setExecutingJobId(jobId);
    
    try {
      const response = await api.post("/scheduler/jobs/execute", {
        job_id: jobId
      });
      
      showSuccess(response.data.message || "Job agendado para execução imediata!");
      
      setTimeout(() => {
        fetchJobs(false);
      }, 1000);
    } catch (err) {
      console.error("Erro ao executar job:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao executar job.";
      showError(errorMsg);
    } finally {
      setExecutingJobId(null);
    }
  };

  const handlePauseJob = async (jobId) => {
    setPausingJobId(jobId);
    
    try {
      const response = await api.post(`/scheduler/jobs/${jobId}/pause`);
      showSuccess(response.data.message || "Job pausado com sucesso!");
      
      await fetchJobs(false);
    } catch (err) {
      console.error("Erro ao pausar job:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao pausar job.";
      showError(errorMsg);
    } finally {
      setPausingJobId(null);
    }
  };

  const handleResumeJob = async (jobId) => {
    setResumingJobId(jobId);
    
    try {
      const response = await api.post(`/scheduler/jobs/${jobId}/resume`);
      showSuccess(response.data.message || "Job retomado com sucesso!");
      
      await fetchJobs(false);
    } catch (err) {
      console.error("Erro ao retomar job:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao retomar job.";
      showError(errorMsg);
    } finally {
      setResumingJobId(null);
    }
  };

  const handleDisableJob = async (jobId) => {
    setDisablingJobId(jobId);
    
    try {
      const response = await api.post(`/scheduler/jobs/${jobId}/disable`);
      showSuccess(response.data.message || "Job desabilitado permanentemente!");
      
      await fetchJobs(false);
    } catch (err) {
      console.error("Erro ao desabilitar job:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao desabilitar job.";
      showError(errorMsg);
    } finally {
      setDisablingJobId(null);
    }
  };

  const handleEnableJob = async (jobId) => {
    setEnablingJobId(jobId);
    
    try {
      const response = await api.post(`/scheduler/jobs/${jobId}/enable`);
      showSuccess(response.data.message || "Job habilitado com sucesso!");
      
      await fetchJobs(false);
    } catch (err) {
      console.error("Erro ao habilitar job:", err);
      const errorMsg = err.response?.data?.detail || "Erro ao habilitar job.";
      showError(errorMsg);
    } finally {
      setEnablingJobId(null);
    }
  };

  const handleViewDetails = (jobId) => {
    setSelectedJobId(jobId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJobId(null);
  };

  const handleManualRefresh = () => {
    refreshData();
    showSuccess("Dados atualizados!");
  };

  const renderContent = () => {
    if (isLoadingJobs) {
      return (
        <LoadingState>
          <FiLoader size={48} color="#2f2f2f" />
          <p>Carregando jobs agendados...</p>
        </LoadingState>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <h3>Erro ao carregar jobs</h3>
          <p>{error}</p>
          <button onClick={() => fetchJobs()}>Tentar novamente</button>
        </ErrorState>
      );
    }

    if (jobs.length === 0) {
      return (
        <EmptyState>
          <h3>Nenhum job agendado</h3>
          <p>Não há jobs configurados no sistema no momento.</p>
        </EmptyState>
      );
    }

    return (
      <JobsGrid>
        {jobs.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            onViewDetails={handleViewDetails}
            onExecute={handleExecuteJob}
            onPause={handlePauseJob}
            onResume={handleResumeJob}
            onDisable={handleDisableJob}
            onEnable={handleEnableJob}
            isExecuting={executingJobId === job.id}
            isPausing={pausingJobId === job.id}
            isResuming={resumingJobId === job.id}
            isDisabling={disablingJobId === job.id}
            isEnabling={enablingJobId === job.id}
          />
        ))}
      </JobsGrid>
    );
  };

  return (
    <CronContainer>
      <PageTitle>Gerenciamento de Jobs Agendados</PageTitle>

      <SchedulerHealth
        health={health}
        isLoading={isLoadingHealth}
        error={healthError}
      />

      <InfoPanel />

      <HeaderSection>
        <RefreshButton
          onClick={handleManualRefresh}
          disabled={isRefreshing}
          $isRotating={isRefreshing}
        >
          <FiRefreshCw size={16} />
          {isRefreshing ? "Atualizando..." : "Atualizar"}
        </RefreshButton>
      </HeaderSection>

      {renderContent()}

      <JobDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        jobId={selectedJobId}
      />

      <ToastManager toasts={toasts} removeToast={removeToast} />
    </CronContainer>
  );
};

export default CronIndexPage;
